import { AlertTriangle } from 'react-feather';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ParticipantFieldType } from '__generated__/graphql';

import { AddressValue } from './Types/AddressInput';
import {
  AuLicenseData, ParticipantField, ParticipantFieldEntry, convertToParticipantFieldEntryInput, validateFieldEntry,
} from './helpers';
import { formatTime } from '../helpers';
import Address from '../Common/Address';
import PhoneNumber from '../PhoneNumber/PhoneNumber';
import UI from '../UI';

const formatAtletiekunieLicense = (value: string | null) => {
  if (value === null) {
    return '';
  }

  const data: AuLicenseData = JSON.parse(value);

  return (
    <UI.Delimit>
      {data.member_id}
      {data.club_name}
    </UI.Delimit>
  );
};

type Renderer = (fieldEntry: ParticipantFieldEntry, isEmpty: boolean) => ReactNode;

const ParticipantFieldValueRenderer: Record<ParticipantFieldType, Renderer> = {
  [ParticipantFieldType.short_text]: (fieldEntry) => <>{fieldEntry.value}</>,
  [ParticipantFieldType.integer]: (fieldEntry) => <>{fieldEntry.value}</>,
  [ParticipantFieldType.iban]: (fieldEntry) => <>{fieldEntry.value}</>,
  [ParticipantFieldType.address]: (fieldEntry, isEmpty) => (
    isEmpty || !fieldEntry.value ? null : <Address {...(JSON.parse(fieldEntry.value) as AddressValue)} />
  ),
  [ParticipantFieldType.phone]: (fieldEntry) => (
    fieldEntry.value ? <PhoneNumber phoneNumber={fieldEntry.value} /> : null
  ),
  [ParticipantFieldType.time]: (fieldEntry) => <>{formatTime(fieldEntry.value)}</>,
  [ParticipantFieldType.au_license]: (fieldEntry) => <>{formatAtletiekunieLicense(fieldEntry.value)}</>,
  [ParticipantFieldType.knwu_license]: (fieldEntry) => <>{fieldEntry.value}</>,
  [ParticipantFieldType.kwbn_member]: (fieldEntry) => <>{fieldEntry.value}</>,
  [ParticipantFieldType.ntfu_member]: (fieldEntry) => <>{fieldEntry.value}</>,
  [ParticipantFieldType.option]: (fieldEntry) => (
    <>
      {fieldEntry.choice_entries.map(({ choice }) => (
        <UI.Span sc={{ muted: !choice.enabled }} key={choice.id}>
          {choice.title}
        </UI.Span>
      ))}
    </>
  ),
  [ParticipantFieldType.selection]: (fieldEntry) => (
    <UI.UL sc={{ mb: 0 }}>
      {fieldEntry.choice_entries.map(({ choice }) => (
        <UI.LI sc={{ muted: !choice.enabled }} key={choice.id}>
          {choice.title}
        </UI.LI>
      ))}
    </UI.UL>
  ),
};

interface ParticipantFieldValueProps {
  fieldEntry: ParticipantFieldEntry;
  field: Pick<ParticipantField, 'id' | 'type' | 'title' | 'required' | 'config' | 'enabled'>;
}

const ParticipantFieldValue = ({ fieldEntry, field }: ParticipantFieldValueProps) => {
  const { t } = useTranslation();

  const valid = validateFieldEntry({
    fieldEntry: convertToParticipantFieldEntryInput({ ...fieldEntry, field: { id: field.id } }),
    field,
  });

  const addressValue = field.type === ParticipantFieldType.address && fieldEntry.value && (
    JSON.parse(fieldEntry.value) as AddressValue
  );

  const isEmpty = (!fieldEntry.value && fieldEntry.choice_entries.length === 0)
    || (addressValue?.enabled === false);

  return (
    <UI.Div sc={{ muted: field.enabled === false }}>
      <UI.Legend sc={{ fontWeight: 500 }}>
        {field.title}
      </UI.Legend>

      {ParticipantFieldValueRenderer[field.type](fieldEntry, isEmpty)}

      {isEmpty && (
        <>
          {(valid || !field.enabled) && (
            <UI.Span sc={{ muted: true }}> &ndash; </UI.Span>
          )}
          {!valid && field.enabled && (
            <UI.Span sc={{ color: 'error' }}>
              <UI.Icon>
                <AlertTriangle />
              </UI.Icon>
              {' '}
              {t('not_entered')}
            </UI.Span>
          )}
        </>
      )}
    </UI.Div>
  );
};

export default ParticipantFieldValue;
